import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Icon, IconButton, Search } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import {
  type ContactBookId,
  type ContactRecordId,
  type UserId,
  type Workflow,
  type WorkflowId,
} from '~types'
import { openInNewTab, reserveNewTab } from '~utils/navigationUtils'
import { loadResourcePermittedOrgIdentitiesWithAdHoc } from '~utils/permissionsUtils'

import './StartWorkflowDialog.scss'

type WorkflowVersionStatus = 'live' | 'draft'

interface StartWorkflowDialogProps {
  open: boolean
  onClose: () => void
  requesterId?: string | null
  owners?: string[]
  contactRecordId?: ContactRecordId | null
  contactBookId?: ContactBookId | null
}

const StartWorkflowDialog: React.FC<StartWorkflowDialogProps> = ({
  open,
  onClose,
  requesterId,
  owners,
  contactRecordId,
  contactBookId,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [selectedStatus, setSelectedStatus] = useState<WorkflowVersionStatus>('live')
  const [processPermittedOrgIdentities, setProcessPermittedOrgIdentities] = useState<string[]>([])
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const user = useSelector((state: RootState) => state?.auth.user)
  const workspaceOrgs = useSelector(selectWorkspaceOrgs)
  const [loadingWorkflowId, setLoadingWorkflowId] = useState<WorkflowId | null>(null)

  const ownersKey = owners ? owners.join(',') : undefined

  const orgsByOrganizationId = useMemo(
    () => new Map(workspaceOrgs.map((org) => [org.organization_id, org])),
    [workspaceOrgs],
  )

  useEffect(() => {
    const loadProcessPolicies = async (): Promise<void> => {
      if (!userId) return
      const processPolicies = await loadResourcePermittedOrgIdentitiesWithAdHoc(
        userId as UserId,
        'processes',
      )
      setProcessPermittedOrgIdentities(processPolicies['read'] ?? [])
    }
    if (open) {
      loadProcessPolicies()
    }
  }, [open, userId])

  const loadWorkflows = async (): Promise<void> => {
    const organizationIdentityIds = workspaceOrgs
      .map((org) => org.id)
      .filter(
        (id) => processPermittedOrgIdentities.includes(id) && (!owners || owners.includes(id)),
      )
    if (!userId || organizationIdentityIds.length === 0) {
      setWorkflows([])
      return
    }

    try {
      const response = await apiZodios.cases_listWorkflows({
        user_id: userId,
        organization_identity_id_list: organizationIdentityIds,
        status: ['active'],
        has_live_version: selectedStatus === 'live',
        contact_book_id: contactBookId ?? undefined,
      })
      setWorkflows(response.workflows)
    } catch (error) {
      console.error('Error loading workflows:', error)
    }
  }

  useEffect(() => {
    if (open) {
      loadWorkflows()
    }
  }, [open, selectedStatus, ownersKey, workspaceOrgs, contactBookId, processPermittedOrgIdentities])

  const filteredWorkflows = useMemo(() => {
    if (!searchQuery) return workflows
    const lowercaseQuery = searchQuery.toLowerCase()
    return workflows.filter((workflow) => workflow.name.toLowerCase().includes(lowercaseQuery))
  }, [workflows, searchQuery])

  const resolveRequesterForOwner = async (ownerId: string): Promise<string | null | undefined> => {
    if (requesterId !== undefined && requesterId !== 'undefined') {
      return requesterId ?? null
    }

    if (!contactRecordId || !userId) {
      return undefined
    }

    try {
      const response = await apiZodios.contacts_getContactRecordIdentity({
        user_id: userId,
        organization_identity_id: ownerId,
        contact_record_id: contactRecordId,
      })
      return response.identity_id
    } catch {
      return null
    }
  }

  const handleWorkflowClick = async (workflow: Workflow): Promise<void> => {
    const ownerId = orgsByOrganizationId.get(workflow.organization)?.id
    if (!userId || !ownerId || loadingWorkflowId) return
    setLoadingWorkflowId(workflow.id as WorkflowId)
    const caseTab = reserveNewTab()

    try {
      const resolvedRequesterId = await resolveRequesterForOwner(ownerId)
      let handle
      if (selectedStatus === 'draft') {
        const response = await apiZodios.cases_createTestCase({
          user_id: userId,
          organization_identity_id: ownerId,
          name: workflow.name,
          workflow_id: workflow.id,
          requester: resolvedRequesterId,
        })
        handle = response.handle
      } else {
        const response = await apiZodios.cases_createCase({
          user_id: userId,
          name: workflow.name,
          status: 'Open',
          organization_identity_id: ownerId,
          requester: resolvedRequesterId,
          assignee: user?.identity_id,
          contact_record_id: resolvedRequesterId && contactRecordId ? contactRecordId : undefined,
          workflow_id: workflow.id,
        })
        handle = response.case.handle
      }
      if (handle) {
        caseTab.navigate(`/cases/${handle}?openForm=first`)
      } else {
        caseTab.close()
      }

      onClose()
      setLoadingWorkflowId(null)
    } catch (error) {
      console.error('Error creating case:', error)
      caseTab.close()
      setLoadingWorkflowId(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className='start-workflow-dialog'>
      <DialogTitle className='start-workflow-dialog__title'>
        Open Case
        <IconButton size='small' onClick={onClose}>
          <Icon fontSize='small' name='close' />
        </IconButton>
      </DialogTitle>
      <Box className='start-workflow-dialog__search-row'>
        <Box className='start-workflow-dialog__search-input'>
          <Search
            placeholder='Search workflows'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
            width='full'
          />
        </Box>
        <ToggleButtonGroup
          value={selectedStatus}
          exclusive
          onChange={(_, value) => {
            if (value) setSelectedStatus(value as WorkflowVersionStatus)
          }}
          size='small'
        >
          <ToggleButton value='live'>
            <Icon name='Sensors' size={16} />
            Live
          </ToggleButton>
          <ToggleButton value='draft'>
            <Icon name='EditOutlined' size={16} />
            Draft
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <List className='start-workflow-dialog__list'>
        {filteredWorkflows.length > 0 ? (
          filteredWorkflows.map((workflow) => {
            const owner = orgsByOrganizationId.get(workflow.organization)
            return (
              <ListItem key={workflow.id} disablePadding>
                <ListItemButton
                  disabled={loadingWorkflowId !== null}
                  onClick={() => handleWorkflowClick(workflow)}
                  className='start-workflow-dialog__list-item-button'
                >
                  <Box className='start-workflow-dialog__list-item-content'>
                    <Box
                      className={[
                        'start-workflow-dialog__workflow-icon',
                        selectedStatus === 'live' && 'start-workflow-dialog__workflow-icon--live',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <Icon name={selectedStatus === 'live' ? 'Sensors' : 'EditOutlined'} />
                    </Box>
                    <Box className='start-workflow-dialog__workflow-info'>
                      <Typography variant='body1' className='start-workflow-dialog__workflow-name'>
                        {workflow.name}
                      </Typography>
                      <Box className='start-workflow-dialog__owner-info'>
                        <Avatar
                          src={owner?.image || ''}
                          alt={owner?.name ?? ''}
                          className='start-workflow-dialog__owner-avatar'
                        />
                        <Typography variant='body2' className='start-workflow-dialog__owner-name'>
                          {owner?.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {loadingWorkflowId === workflow.id && (
                    <LinearProgress className='start-workflow-dialog__loading-bar' />
                  )}
                </ListItemButton>
              </ListItem>
            )
          })
        ) : (
          <Box className='start-workflow-dialog__empty-state'>
            <Typography>No workflows found</Typography>
          </Box>
        )}
      </List>
      <Box className='start-workflow-dialog__footer'>
        <Typography variant='body2' color='textSecondary'>
          Don't want to start a workflow?{' '}
          <Link
            component='button'
            variant='body2'
            color='secondary'
            sx={{ verticalAlign: 'baseline' }}
            onClick={() => {
              onClose()
              const params = new URLSearchParams()
              if (requesterId && requesterId !== 'undefined') {
                params.set('applicant', requesterId)
              }
              if (owners && owners.length > 0) {
                params.set('organization', owners[0])
              }
              const query = params.toString()
              openInNewTab(`/create-case/ViaSite${query ? `?${query}` : ''}`)
            }}
          >
            Open a manual case instead
          </Link>
        </Typography>
      </Box>
    </Dialog>
  )
}

export default StartWorkflowDialog
